import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import type { PropsWithChildren } from 'react';
import { twMerge } from 'tailwind-merge';
import { tv } from 'tailwind-variants';

import { Card } from '@repo/components/card';
import { Disclosure, DisclosureHeader, DisclosurePanel } from '@repo/components/disclosure';
import { Heading } from '@repo/components/heading';

import { capitalize } from '@repo/utils/capitalised';
import { sum } from '@repo/utils/sum';
import { useNumberFormatter } from '@repo/utils/use-number-formatter';

import { ContentHeader } from '~/features/content-header/content-header.tsx';
import { PageError } from '~/features/page-error/page-error.tsx';
import { queryCertificate } from '~/services/certificate/certificate.ts';
import type { AdditionalFields as AdditionalFieldsType, Certificate, Emission } from '~/services/certificate/types.ts';

export const Route = createFileRoute('/project/$project/(certificates)/certificates/$cert')({
  loader: async ({ context: { queryClient }, params: { project, cert: id } }) =>
    queryClient.ensureQueryData(queryCertificate(project, id)),
  component: RouteCertificateDetails,
  errorComponent: () => <PageError msg="An error occurred while loading certificate." />,
});

const {
  cardTitle,
  textFaded,
  sectionHeading,
  propertyGrid,
  propertyValue,
} = (tv({
  slots: {
    cardTitle: 'font-medium',
    textFaded: 'text-sm text-secondary-500',
    sectionHeading: 'text-2xl font-light',
    propertyGrid: 'grid @md:grid-cols-2 gap-4',
    propertyValue: 'border-l-2 border-l-primary-800 py-1/2 pl-2 whitespace-nowrap font-medium',
  },
}))();

function RouteCertificateDetails() {
  const numberFormatter = useNumberFormatter({ minimumFractionDigits: 3, maximumFractionDigits: 3 });
  const formatNumber = (num: number) => numberFormatter.format(num);

  const { project: projectId, cert: id } = Route.useParams();
  const { data: certificate } = useSuspenseQuery(queryCertificate(projectId, id));
  const transport = certificate.transport;
  const totalLoaded = transport ? sum(transport.transportSegments.map(segment => segment.quantityLoaded)) : 0;
  const totalUnloaded = transport ? sum(transport.transportSegments.map(segment => segment.quantityUnloaded)) : 0;
  const firstTransportSegment = transport?.transportSegments[0];
  const lastTransportSegment = transport?.transportSegments.at(-1);
  const directEmissions = calculateDirectEmissions(certificate, 'directEmissions');
  const associatedEmissions = calculateDirectEmissions(certificate, 'associatedEmissions');

  return (
    <div className="flex flex-col pb-6 max-w-7xl @container">
      <div className="grid @5xl:grid-cols-2 gap-y-8 gap-x-4">
        <ContentHeader className="col-span-full" back={{ to: '..' }}>Storage Certificate</ContentHeader>
        <div className="@5xl:col-span-2 grid @5xl:grid-cols-2 gap-4">
          <section className="flex flex-col gap-1 grow shrink">
            <Heading className={sectionHeading()}>Issued by</Heading>
            <Card>
              <div className="flex flex-col gap-1">
                <p className={cardTitle()}>{certificate.certificateIssuer.organization}</p>
                <a href={certificate.certificateIssuer.organizationUrl} className={twMerge(textFaded(), 'underline text-blue-700')}>
                  {certificate.certificateIssuer.organizationUrl}
                </a>
                <p className={textFaded()}>
                  ID:
                  {certificate.certificateIssuer.organizationId}
                </p>
              </div>
            </Card>
          </section>

          <section className="flex flex-col gap-1 grow shrink">
            <Heading className={sectionHeading()}>Issued to</Heading>
            <Card>
              <div className="flex flex-col gap-1">
                <p className={cardTitle()}>{certificate.certificateRecipient.organization}</p>
                <a href={certificate.certificateRecipient.organizationUrl} className={twMerge(textFaded(), 'underline text-blue-700')}>
                  {certificate.certificateRecipient.organizationUrl}
                </a>
                <p className={textFaded()}>
                  ID:
                  {certificate.certificateRecipient.organizationId}
                </p>
              </div>
            </Card>
          </section>
        </div>

        <section className="grid grid-cols-subgrid col-span-full gap-y-2">
          <Heading className={twMerge(sectionHeading(), 'col-span-full')}>Summary</Heading>
          <Card className="@container">
            <div className="flex flex-col gap-1">
              <p className={cardTitle()}>
                {firstTransportSegment
                  ? `${firstTransportSegment.startLocation.description} to`
                  : ''}
                {' '}
                {certificate.storage.location.description}
              </p>
              <p className={textFaded()}>
                Issued at
                {certificate.metadata.sealedDate}
              </p>
            </div>

            <div className={propertyGrid()}>
              {firstTransportSegment && (
                <Property
                  label={`Loaded (${firstTransportSegment.quantityType})`}
                  value={`${formatNumber(totalLoaded)} ${firstTransportSegment.quantityUnit}`}
                />
              )}
              <Property
                label={`Stored (${certificate.storage.quantityType})`}
                value={`${formatNumber(certificate.storage.quantityStored)} ${certificate.storage.quantityUnit}`}
              />
              <Property label="Direct emissions" value={`${formatNumber(directEmissions.tCO2 ?? 0)} tCO2`} />
              <Property label="Associated emissions" value={`${formatNumber(associatedEmissions.tCO2 ?? 0)} tCO2`} />
              <Property label="CO2 Purity" value={toPct(certificate.storageTerminal.co2Purity)} />
            </div>
          </Card>
        </section>

        <section className="grid grid-cols-subgrid col-span-full gap-y-2">
          <Heading className={twMerge(sectionHeading(), 'col-span-full')}>Captured</Heading>
          {certificate.captures.length === 0 && <p>No capture data reported by recipient.</p>}

          {certificate.captures.map(capture => (
            <Card key={capture.id} className="@container">
              <div className="flex flex-col gap-1">
                <p className={cardTitle()}>Captured CO2</p>
                <p className={textFaded()}>
                  ID:
                  {capture.id}
                </p>
              </div>

              <div className={propertyGrid()}>
                <Property label="Source" value={capture.co2Source} />
                <Property label={`Captured (${capture.quantityType})`} value={`${formatNumber(capture.quantityValue)} ${capture.quantityUnit}`} />
              </div>

              <CollapsibleSection title="Details">
                <div className="flex flex-col gap-4">
                  <div className={propertyGrid()}>
                    <Property label="Start time" value={capture.startTime} />
                    <Property label="End time" value={capture.endTime} />
                    <Property label="CO2 Purity" value={toPct(capture.co2Purity)} />
                  </div>
                  <Emissions entity={capture} />
                </div>
              </CollapsibleSection>

              <AdditionalFields entity={capture} />
            </Card>
          ))}
        </section>

        {transport && (
          <section className="grid grid-cols-subgrid col-span-full gap-y-2">
            <Heading className={twMerge(sectionHeading(), 'col-span-full')}>Transport</Heading>

            <Card className="@container">
              <div className="flex flex-col gap-1">
                <p className={cardTitle()}>
                  {firstTransportSegment?.startLocation.description ?? 'Start location'}
                  {' '}
                  to
                  {' '}
                  {lastTransportSegment?.endLocation.description ?? 'End location'}
                </p>
                <p className={textFaded()}>
                  ID:
                  {transport.id}
                </p>
              </div>

              <div className={propertyGrid()}>
                <Property label="Start time" value={transport.startTime} />
                <Property label="End time" value={transport.endTime} />
                {firstTransportSegment && (
                  <>
                    <Property label={`Loaded (${firstTransportSegment.quantityType})`} value={`${formatNumber(totalLoaded)} ${firstTransportSegment.quantityUnit}`} />
                    <Property label={`Unloaded (${firstTransportSegment.quantityType})`} value={`${formatNumber(totalUnloaded)} ${firstTransportSegment.quantityUnit}`} />
                  </>
                )}
              </div>

              {transport.transportSegments.map(segment => (
                <CollapsibleSection key={segment.id} title={`${segment.startLocation.description} to ${segment.endLocation.description}`}>
                  <div className="flex flex-col gap-4">
                    <div className={propertyGrid()}>
                      <Property label="Responsible party" value={segment.responsiblePartyName} />
                      <Property label="Method" value={capitalize(segment.transportMethod)} />
                      <Property label="Origin" value={segment.startLocation.description} />
                      <Property label="Destination" value={segment.endLocation.description} />
                      <Property label="Start time" value={segment.startTime} />
                      <Property label="End time" value={segment.endTime} />
                      <Property label={`Loaded (${segment.quantityType})`} value={`${formatNumber(segment.quantityLoaded)} ${segment.quantityUnit}`} />
                      <Property label={`Unloaded (${segment.quantityType})`} value={`${formatNumber(segment.quantityUnloaded)} ${segment.quantityUnit}`} />
                    </div>

                    <Emissions entity={segment} />
                    <AdditionalFields entity={segment} />
                  </div>
                </CollapsibleSection>
              ))}

              <AdditionalFields entity={transport} />
            </Card>
          </section>
        )}

        <section className="grid grid-cols-subgrid col-span-full gap-y-2">
          <Heading className={twMerge(sectionHeading(), 'col-span-full')}>Temporary storage</Heading>
          <Card className="@container">
            <div className="flex flex-col gap-1">
              <p className={cardTitle()}>{certificate.storageTerminal.location.description}</p>
              <p className={textFaded()}>
                ID:
                {certificate.storageTerminal.id}
              </p>
            </div>

            <div className={propertyGrid()}>
              <Property label={`Received (${certificate.storageTerminal.quantityType})`} value={`${formatNumber(certificate.storageTerminal.quantityReceived)} ${certificate.storageTerminal.quantityUnit}`} />
              <Property label={`Injected (${certificate.storageTerminal.quantityType})`} value={`${formatNumber(certificate.storageTerminal.quantityInjected)} ${certificate.storageTerminal.quantityUnit}`} />
            </div>

            <CollapsibleSection title="Details">
              <div className="flex flex-col gap-4">
                <div className={propertyGrid()}>
                  <Property label="Start time" value={certificate.storageTerminal.startTime} />
                  <Property label="End time" value={certificate.storageTerminal.endTime} />
                  <Property label="CO2 Purity" value={toPct(certificate.storageTerminal.co2Purity)} />
                </div>

                <Emissions entity={certificate.storageTerminal} />
              </div>
            </CollapsibleSection>

            <AdditionalFields entity={certificate.storageTerminal} />
          </Card>
        </section>

        <section className="grid grid-cols-subgrid col-span-full gap-y-2">
          <Heading className={twMerge(sectionHeading(), 'col-span-full')}>Storage</Heading>
          <Card className="@container">
            <div className="flex flex-col gap-1">
              <p className={cardTitle()}>{certificate.storage.location.description}</p>
              <p className={textFaded()}>
                ID:
                {certificate.storage.id}
              </p>
            </div>

            <div className={propertyGrid()}>
              <Property label={`Injected (${certificate.storage.quantityType})`} value={`${formatNumber(certificate.storage.quantityInjected)} ${certificate.storage.quantityUnit}`} />
              <Property label={`Stored (${certificate.storage.quantityType})`} value={`${formatNumber(certificate.storage.quantityStored)} ${certificate.storage.quantityUnit}`} />
            </div>

            <CollapsibleSection title="Details">
              <div className="flex flex-col gap-4">
                <div className={propertyGrid()}>
                  <Property label="Injected time" value={certificate.storage.injectedTime} />
                  <Property label="Stored time" value={certificate.storage.storedTime} />
                </div>

                <Emissions entity={certificate.storage} />
              </div>
            </CollapsibleSection>

            <AdditionalFields entity={certificate.storage} />
          </Card>
        </section>

        <footer className="flex flex-col col-span-full gap-4 border-t border-secondary-300 py-6">
          <div className="flex flex-col gap-1">
            <p className={textFaded()}>
              ID:
              {certificate.metadata.storageCertificateId}
            </p>
            <p className={textFaded()}>
              Certificate version:
              {certificate.certificateVersion}
            </p>
          </div>
          <div className="flex flex-col gap-1">
            <p className={textFaded()}>
              Created:
              {certificate.metadata.createdDate}
            </p>
            <p className={textFaded()}>
              Issued:
              {certificate.metadata.sealedDate}
            </p>
            <p className={textFaded()}>
              Expires:
              {certificate.metadata.expiryDate}
            </p>
          </div>
          <div className="flex flex-col gap-1">
            <p className={textFaded()}>
              MRV spec version:
              {certificate.metadata.mrvVersion}
            </p>
            <a className={textFaded()} href={certificate.metadata.mrvUrl}>
              {certificate.metadata.mrvUrl}
              {' '}
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
}

function toPct(p: number): string {
  return `${(p * 100)}%`;
}

function Property({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <p className={textFaded()}>{label}</p>
      <p className="border-l-2 border-l-primary-800 py-1/2 pl-2 whitespace-nowrap font-medium">{value}</p>
    </div>
  );
}

function AdditionalFields({ entity }: { entity: { additionalFields: AdditionalFieldsType } }) {
  return [
    entity.additionalFields.comment1,
    entity.additionalFields.comment2,
    entity.additionalFields.comment3,
  ]
    .map(comment => comment
      && <p key={comment} className={textFaded()}>{comment}</p>);
}

function Emissions(
  { entity: { directEmissions, associatedEmissions } }:
  { entity: { directEmissions: Emission[]; associatedEmissions: Emission[] } },
) {
  const numberFormatter = useNumberFormatter({ minimumFractionDigits: 3, maximumFractionDigits: 3 });
  const formatNumber = (num: number) => numberFormatter.format(num);
  return (
    <>
      <div className="flex flex-col gap-1">
        <p className={textFaded()}>Direct emissions</p>
        {directEmissions.length === 0 && <p className={twMerge(propertyValue(), 'font-normal')}>No direct emissions</p> }
        <div className={twMerge('flex flex-col gap-2', propertyValue())}>

          {directEmissions.map(emission => (
            <div className="flex justify-between w-full" key={emission.description}>
              <p className="font-normal">
                {emission.description}
                :
              </p>
              <p className="tabular-nums font-medium">
                {formatNumber(emission.value)}
                {' '}
                {emission.unit}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <p className={textFaded()}>Associated emissions</p>
        {associatedEmissions.length === 0 && <p className={twMerge(propertyValue(), 'font-normal')}>No associated emissions</p> }
        <div className={twMerge('flex flex-col gap-2', propertyValue())}>

          {associatedEmissions.map(emission => (
            <div className="flex justify-between w-full" key={emission.description}>
              <p className="font-normal">
                {emission.description}
                :
              </p>
              <p className="tabular-nums font-medium">
                {formatNumber(emission.value)}
                {' '}
                {emission.unit}
              </p>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function CollapsibleSection({ title, children }: PropsWithChildren<{ title: string }>) {
  return (
    <Disclosure level={2} className="my-2">
      <DisclosureHeader>
        {title}
      </DisclosureHeader>
      <DisclosurePanel>
        {children}
      </DisclosurePanel>
    </Disclosure>
  );
}

function calculateDirectEmissions(certificate: Certificate, type: 'directEmissions' | 'associatedEmissions'): Record<Emission['unit'], number | undefined> {
  return [...certificate.captures, ...(certificate.transport?.transportSegments ?? []), certificate.storageTerminal, certificate.storage]
    .map(entity => entity[type])
    .reduce((result, emissions) => {
      const currentEmissions = sumEmissions(emissions);

      if (!currentEmissions.tCO2) {
        return result;
      }

      if (!result.tCO2) {
        result.tCO2 = currentEmissions.tCO2;
      }
      else {
        result.tCO2 += currentEmissions.tCO2;
      }

      return result;
    }, {} as Record<Emission['unit'], number>);
}

function sumEmissions(emissions: Emission[]) {
  return emissions.reduce((result, emission) => {
    if (!result[emission.unit]) {
      result[emission.unit] = emission.value || 0;
    }
    else {
      result[emission.unit] += emission.value || 0;
    }

    return result;
  }, {} as Record<Emission['unit'], number>);
}
